/*!
* Copyright (c) Microsoft Corporation.
* All rights reserved. See LICENSE in the project root for license information.
*/
 
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IPriceComponentResources,
    ISwatchItem,
    PriceComponent,
    ProductComponentSwatchComponent,
    RatingComponent
} from '@msdyn365-commerce/components';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageSettings,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    ProductDimension,
    ProductPrice,
    ProductSearchResult,
    ProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
 
import { getProductClassificationDisplayValue, isPvoProductClassification } from '../../utils/product-classification';
 
export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult; productPrice?: ProductPrice }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
}
 
export interface IProductComponent extends IComponent<IProductComponentProps> {}
 
const PriceComponentActions = {};
 
/**
* Gets the product page url from the default swatch selected.
* @param  productData - Product card to be rendered.
* @returns The default color swatch selected if any.
*/
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }
 
    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
 
    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;
 
    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }
 
    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}
 
/**
* Gets the product image from the default swatch selected.
* @param  coreContext - Context of the module using the component.
* @param  productData - Product card to be rendered.
* @returns The product card image url.
*/
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }
 
    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}
 
/**
* Updates the product url link to product details page.
* @param  productDetailsPageUrl - Product page url.
* @param  coreContext - Context of the module using the component.
* @param  queryString - Querystring to be added to the URL.
* @returns The update product page url.
*/
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }
 
    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}
 
/**
* Gets the react node for product unit of measure display.
* @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
* @returns The node representing markup for unit of measure component.
*/
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}
 
/**
* Gets the react node for product availability.
* @param inventoryAvailabilityLabel - The product information.
* @returns The node representing markup for product availability.
*/
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }
 
    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}
 
/**
* Scalar / extension display value for a product attribute (PLP / browse).
*/
function getAttributeDisplayValue(attribute: AttributeValue): string {
    if (attribute.TextValue) {
        return attribute.TextValue;
    }
 
    const extensionValue = attribute.ExtensionProperties?.find(property => !!property?.Value)?.Value;
    if (!extensionValue) {
        return '';
    }
 
    if (extensionValue.StringValue) {
        return extensionValue.StringValue;
    }
 
    if (extensionValue.BooleanValue !== undefined && extensionValue.BooleanValue !== null) {
        return extensionValue.BooleanValue ? 'Yes' : 'No';
    }
 
    if (extensionValue.DecimalValue !== undefined && extensionValue.DecimalValue !== null) {
        return extensionValue.DecimalValue.toString();
    }
 
    if (extensionValue.IntegerValue !== undefined && extensionValue.IntegerValue !== null) {
        return extensionValue.IntegerValue.toString();
    }
 
    if (extensionValue.LongValue !== undefined && extensionValue.LongValue !== null) {
        return extensionValue.LongValue.toString();
    }
 
    return '';
}
 
/**
* Gets the react node for product attributes on PLP.
* @param attributeValues - Product attribute values.
* @returns The node representing markup for product attributes.
*/
function renderProductAttributes(attributeValues?: AttributeValue[]): JSX.Element | null {
    if (!ArrayExtensions.hasElements(attributeValues)) {
        return null;
    }
 
    const normalizeAttributeName = (name: string): string => name.replace(/[^a-z0-9]/gi, '').toLocaleLowerCase();
 
    const attributeRows = attributeValues
    .map(attribute => {
        const name = attribute.Name ?? attribute.KeyName ?? '';
        const value = getAttributeDisplayValue(attribute);
 
        if (!name || !value) {
            return null;
        }
 
        // Remove "JDE" prefix from the display name
        const displayName = name.replace(/^JDE\s+/i, '');
 
        const normalizedName = normalizeAttributeName(name);
        const normalizedKeyName = normalizeAttributeName(attribute.KeyName ?? '');
 
        return {
            name: displayName,
            value,
            normalizedName,
            normalizedKeyName
        };
    })
    .filter(
        (attribute): attribute is {
            name: string;
            value: string;
            normalizedName: string;
            normalizedKeyName: string;
        } => attribute !== null
    );
 
    const classificationRaw = getProductClassificationDisplayValue(attributeValues);
    const isPvoItem = isPvoProductClassification(classificationRaw);
 
    const sharedVisibleAttributes = new Set(['jdeitemid', 'hazmat','itemid']);
    const pvoOnlyVisibleAttributes = new Set(['bacteriaandvirus']);
    const neverDisplayAttributes = new Set([
        'productclassification',
        'packagesize',
        'storagetemperature',
        'casedescription',
        'quantityperpallet',
        'vsiclassification',
        'vsiproductclassification'
    ]);
 
    const visibleAttributes = attributeRows.filter(attribute => {
        const normalizedNames = [attribute.normalizedName, attribute.normalizedKeyName];
        const matchesAttribute = (set: Set<string>): boolean => normalizedNames.some(normalized => set.has(normalized));
        const isBacteriaAndVirusAttribute = normalizedNames.some(
            normalized => normalized.includes('bacteria') && normalized.includes('virus')
        );
 
        if (matchesAttribute(neverDisplayAttributes)) {
            return false;
        }
 
        if (matchesAttribute(sharedVisibleAttributes)) {
            return true;
        }
 
        if (isPvoItem && matchesAttribute(pvoOnlyVisibleAttributes)) {
            return true;
        }
 
        if (isPvoItem && isBacteriaAndVirusAttribute) {
            return true;
        }
 
        return false;
    });
 
    if (!ArrayExtensions.hasElements(visibleAttributes)) {
        return null;
    }
    return (
        <div className='msc-product__attributes'>
            {visibleAttributes.map(attribute => (
                <div
                    className={`msc-product__attribute${
                        attribute.normalizedName === 'hazmat' || attribute.normalizedKeyName === 'hazmat'
                            ? ' msc-product__attribute--hazmat'
                            : ''
                    }`}
                    key={`${attribute.name}-${attribute.value}`}
                >
                    <span className='msc-product__attribute-name'>{attribute.name}: </span>
                    <span className='msc-product__attribute-value'>{attribute.value}</span>
                </div>
            ))}
        </div>
    );
}
 
/**
* Renders product comparison button similar to the quick view button.
* @param productComparisonButton - React element of the button.
* @param product - Current product info.
* @param catalogId - Current catalog.
* @returns React element for the specific product.
*/
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}
 
/**
* Gets the product page url from the default swatch selected.
* @param  coreContext - Context of the module using the component.
* @param productUrl - Product page url for the product card.
* @param  productData - Product card to be rendered.
* @returns The product card image url.
*/
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }
 
    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
 
    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}
 
const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel
}) => {
    const product = data.product;
    const productPrice = data.productPrice;
 
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
 
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;
 
    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
 
    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );
 
    if (!product) {
        return null;
    }
 
    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }
 
    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }
 
        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }
 
                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }
 
    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }
 
    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());
 
    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
 
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    return (
        <>
            {isEnabledProductDescription ? (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__title_description'>
                            <h2 className='msc-product__title__text'>{product.Name}</h2>
                            {renderPrice(
                                context,
                                typeName,
                                id,
                                product,
                                savingsText,
                                freePriceText,
                                originalPriceText,
                                currentPriceText,
                                isPriceMinMaxEnabled,
                                priceResources,
                                productPrice
                            )}
                            <h5>Unit of Measure: </h5>
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                            {renderProductAttributes(product.AttributeValues)}
                            {renderDescription(product.Description)}
                        </div>
                    </div>
                </a>
            ) : (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
                    <div className='msc-product__image'>
                        {renderProductPlacementImage(
                            imageSettings,
                            context.request.gridSettings,
                            productImageUrl,
                            product.PrimaryImageUrl,
                            product.Name,
                            context
                        )}
                    </div>
                    <div className='msc-product__details'>
                        <h2 className='msc-product__title'>{product.Name}</h2>
                        {renderPrice(
                            context,
                            typeName,
                            id,
                            product,
                            savingsText,
                            freePriceText,
                            originalPriceText,
                            currentPriceText,
                            isPriceMinMaxEnabled,
                            priceResources,
                            productPrice
                        )}
                        <div className='msc-product__unit-of-measure-wrap'>
                            <h5>Unit of Measure: </h5>
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                        </div>
                        {renderProductAttributes(product.AttributeValues)}
                    </div>
                </a>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
 
            {/* ✅ Custom footer always visible under card */}
            <div className='phibro-card-footer'>
                <div className='phibro-card-button'>
                    <a href={productUrl}>View details</a>
                </div>
            </div>
        </>
    );
};
 
function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}
 
function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}
 
function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}
 
function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}
 
function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }
 
    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);
 
    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}
 
function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    productSearchResult: ProductSearchResult,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    productPrice?: ProductPrice
): JSX.Element | null {
    const product: SimpleProduct = {
        // This is a fake product. The product type is only used for price component to determine if it should show price range or not.
        ProductTypeValue: productSearchResult.IsMasterProduct ? ProductType.Master : ProductType.Standalone
    } as SimpleProduct;
 
    const price: ProductPrice = productPrice ?? {
        BasePrice: productSearchResult.BasePrice,
        AdjustedPrice: productSearchResult.Price,
        CustomerContextualPrice: productSearchResult.Price,
        MaxVariantPrice: productSearchResult.MaxVariantPrice ? productSearchResult.MaxVariantPrice : productSearchResult.Price,
        MinVariantPrice: productSearchResult.MinVariantPrice ? productSearchResult.MinVariantPrice : productSearchResult.Price
    };
 
    return (
        <PriceComponent
            context={context}
            id={id}
            typeName={typeName}
            product={product}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
            priceResources={priceResources}
        />
    );
}
 
/** Resolve media filenames independently of product filenames and product extension fallbacks. */
function getProductTilePlaceholderUrl(placeholder: string, baseImageUrl?: string): string | undefined {
    if (/^(?:https?:)?\/\//i.test(placeholder)) {
        return placeholder;
    }
    if (!baseImageUrl) {
        return undefined;
    }
    try {
        const url = new URL(baseImageUrl);
        if (url.pathname.endsWith('/imageFileData/search')) {
            url.searchParams.set('fileName', placeholder);
            url.searchParams.delete('fallback');
            return url.toString();
        }
        return new URL(placeholder, baseImageUrl).toString();
    } catch {
        return undefined;
    }
}
 
interface IProductImageWithFallbackProps {
    sources: string[];
    altText: string;
}
 
const ProductImageWithFallback: React.FC<IProductImageWithFallbackProps> = props => {
    const [sourceIndex, setSourceIndex] = useState(0);
    const imgRef = React.useRef<HTMLImageElement>(null);
    const source = props.sources[sourceIndex];

    // Each keyed image owns one request. Ignore duplicate errors from an older request.
    const handleImageError = () => {
        console.log('[PLP image error]', { failed: source, sourceIndex, allSources: props.sources });
        setSourceIndex(currentIndex => (currentIndex === sourceIndex ? currentIndex + 1 : currentIndex));
    };

    // Catch images that already failed before React hydrated (SSR), where onError never fires.
    React.useEffect(() => {
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth === 0) {
            handleImageError();
        }
    }, [source]);

    return (
        <div className='msc-image-container'>
            {source ? (
                <img
                    key={source}
                    ref={imgRef}
                    className='msc-main_image msc_image'
                    src={source}
                    alt={props.altText}
                    onError={handleImageError}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            ) : (
                <div className='msc-empty_image msc_image' />
            )}
        </div>
    );
};

const IMAGE_EXT_REGEX = /\.(?:jpg|jpeg|png|webp)$/i;
const IMAGE_EXTENSIONS = ['jpg', 'png', 'webp'];

/** Turns a raw image path or URL into one request URL per candidate file name. */
function buildProductImageSources(rawUrl?: string, baseImageUrl?: string): string[] {
    if (!rawUrl) {
        return [];
    }

    let fileName: string;
    let templateUrl: URL | undefined;

    try {
        if (/^(?:https?:)?\/\//i.test(rawUrl)) {
            const url = new URL(rawUrl);
            const name = url.searchParams.get('fileName');
            if (!url.pathname.endsWith('/imageFileData/search') || !name) {
                return [rawUrl]; // not a CMS media URL, use as is
            }
            templateUrl = url;
            fileName = decodeURIComponent(name);
        } else {
            if (!baseImageUrl) {
                return [];
            }
            templateUrl = new URL(baseImageUrl);
            fileName = rawUrl;
        }
        templateUrl.searchParams.delete('fallback');
    } catch {
        return [];
    }

    const cleanName = fileName.replace(/^\/+/, '');              // Products/00004527_000_001.png
    const bareName = cleanName.split('/').pop() ?? cleanName;    // 00004527_000_001.png
    const names = [cleanName, bareName, `/${cleanName}`];

    const withExtensions = names.flatMap(name =>
        IMAGE_EXT_REGEX.test(name)
            ? [name, ...IMAGE_EXTENSIONS.map(ext => name.replace(IMAGE_EXT_REGEX, `.${ext}`))]
            : [name]
    );

    return withExtensions
        .filter((name, index, all) => !!name && all.indexOf(name) === index)
        .map(name => {
            const candidate = new URL(templateUrl!.toString());
            candidate.searchParams.set('fileName', name);
            return candidate.toString();
        });
}
 
function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const placeholderImage = (contextConfig as string | undefined)?.trim();
    const placeholderUrl = placeholderImage
        ? getProductTilePlaceholderUrl(placeholderImage, context?.request.apiSettings.baseImageUrl)
        : undefined;
    const baseImageUrl = context?.request.apiSettings.baseImageUrl;

const sources = [
    ...buildProductImageSources(imageUrl, baseImageUrl),
    ...buildProductImageSources(fallbackImageUrl, baseImageUrl),
    placeholderUrl
].filter(
    (source, index, allSources): source is string => !!source && allSources.indexOf(source) === index
);
    if (!sources.length) {
        return null;
    }
    return (
        <ProductImageWithFallback
            key={JSON.stringify(sources)}
            sources={sources}
            altText={altText || ''}
        />
    );
}
 
export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });
 
export default ProductComponent;